import React, { useEffect, useMemo, useState } from 'react';
import { Product, ProductHistoryEntry } from '../types';
import * as db from '../services/db';
import { formatDateTime } from '../services/datetime';
import '../pages/Sections.css';
import './ProductHistoryModal.css';

// Product Master change trail viewer. With a product selected it shows that
// product's history; otherwise the most recent changes across all products.
// Entries come from db.logProductHistory (created / updated / deleted with
// field-level diffs); stock movements from sales/GR have their own trail.

interface Props {
  product: Product | null;
  onClose: () => void;
}

const ACTION_LABEL: Record<string, string> = { created: 'NEW', updated: 'UPD', deleted: 'DEL' };

const ProductHistoryModal: React.FC<Props> = ({ product, onClose }) => {
  const [all, setAll] = useState<ProductHistoryEntry[]>([]);
  useEffect(() => {
    let alive = true;
    db.getProductHistory().then((h) => {
      if (alive) setAll(h);
    });
    return () => {
      alive = false;
    };
  }, []);
  const entries = useMemo(() => {
    const scoped = product ? all.filter((e) => e.productId === product.id) : all;
    return [...scoped].reverse().slice(0, 200);
  }, [product, all]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="section-card ph-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ph-head">
          <h2>{product ? `History · ${product.name}` : 'Product History'}</h2>
          <button className="btn-grey" onClick={onClose}>✕</button>
        </div>

        {entries.length === 0 ? (
          <p className="muted">
            No changes recorded yet{product ? ' for this product' : ''}. History starts from when a
            product is added, updated or deleted.
          </p>
        ) : (
          <div className="ph-scroll">
            <table className="section-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th></th>
                  {!product && <th>Product</th>}
                  <th>Changes</th>
                  <th>User</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className="ph-time">{formatDateTime(e.timestamp)}</td>
                    <td>
                      <span className={`ph-badge ${e.action}`}>{ACTION_LABEL[e.action]}</span>
                    </td>
                    {!product && <td className="ph-name">{e.productName}</td>}
                    <td>
                      {e.action === 'updated' && e.changes?.length ? (
                        <ul className="ph-changes">
                          {e.changes.map((c, i) => (
                            <li key={i}>
                              <strong>{c.field}</strong>: {c.from} → {c.to}
                            </li>
                          ))}
                        </ul>
                      ) : e.action === 'created' ? (
                        'Product added'
                      ) : (
                        'Product deleted'
                      )}
                    </td>
                    <td className="ph-user">{e.userName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductHistoryModal;
